import type { KpiDrillTarget } from '@/router/routes';

import type { DashboardMetrics } from './dashboard-metrics';
import { fmtMoney } from './dashboard-metrics';

/**
 * The 4-up KPI row (SCRUM-326) — v6 `.dash-kpi` spec: bg-4 card, r-lg,
 * 16px padding; label 0.8125rem text-2; value Exo 2 1.875rem/600; sub
 * 0.75rem text-3; optional mono uppercase trend chip with a small arrow,
 * green for upward trends. Collapses to 2-up below 1080px.
 *
 * SCRUM-847: three cards drill through to Campaigns pre-filtered to the card's
 * population (Ryan's app-shell 9 design). Potential Revenue → Relationship Gap,
 * Deposits at Risk → Flight Risk, Customers Reached → contacted-in-last-30-days.
 * Product Conversions stays display-only. When `onDrill` is omitted the cards
 * render exactly as before (non-interactive) — a safe default for any consumer
 * that does not wire navigation.
 */
export function KpiRow({
  metrics,
  onDrill,
}: {
  metrics: DashboardMetrics;
  onDrill?: (target: KpiDrillTarget) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-4 min-[1081px]:grid-cols-4">
      <KpiCard
        label="Potential Revenue"
        value={fmtMoney(metrics.potentialRevenue)}
        sub={`${String(metrics.opportunities)} clear product matches`}
        drill={
          onDrill
            ? () => {
                onDrill('relgap');
              }
            : undefined
        }
      />
      <KpiCard
        label="Deposits at Risk"
        value={fmtMoney(metrics.flightDeposits)}
        sub={`${String(metrics.flightCount)} flight-risk customers`}
        drill={
          onDrill
            ? () => {
                onDrill('flight');
              }
            : undefined
        }
      />
      <KpiCard
        label="Customers Reached"
        value={String(metrics.reached30)}
        sub="reached in the last 30 days"
        tag="30 days"
        drill={
          onDrill
            ? () => {
                onDrill('reached');
              }
            : undefined
        }
      />
      <KpiCard
        label="Product Conversions"
        value={String(metrics.productConversions)}
        sub={`products acquired · ${String(metrics.referralDriven)} referral-driven`}
        tag="this year"
      />
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
  tag,
  drill,
}: {
  label: string;
  value: string;
  sub: string;
  tag?: string;
  /** When set, the card is a clickable drill-through to Campaigns. */
  drill?: (() => void) | undefined;
}) {
  const body = (
    <>
      <div className="text-text-2 text-[0.8125rem]">{label}</div>
      <div className="font-display text-text-0 text-3xl leading-[1.1] font-semibold">{value}</div>
      <div className="text-text-3 text-xs">{sub}</div>
      {tag && (
        <div className="text-success mt-1.5 inline-flex items-center gap-[5px] font-mono text-[0.625rem] font-bold tracking-[0.05em] uppercase">
          <svg
            viewBox="0 0 15 12"
            width="13"
            height="11"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 9l3-3 2 2 4-4" />
            <polyline points="9 4 12 4 12 7" />
          </svg>
          {tag}
        </div>
      )}
    </>
  );

  if (!drill) {
    return (
      <div className="bg-bg-4 border-border-0 rounded-tk-lg flex flex-col gap-1 border p-4">
        {body}
      </div>
    );
  }

  // SCRUM-847: clickable drill-through. Button semantics + keyboard (Enter/Space handled
  // natively by <button>), hover/focus lift, and a "View in Campaigns →" CTA that fades in
  // on hover/focus (pinned to the card bottom via mt-auto so it aligns across cards).
  return (
    <button
      type="button"
      onClick={drill}
      className="group bg-bg-4 border-border-0 rounded-tk-lg hover:border-border-2 hover:shadow-tk-sm focus-visible:outline-brand-primary flex cursor-pointer flex-col gap-1 border p-4 text-left transition-all hover:-translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2"
    >
      {body}
      <div className="text-text-3 group-hover:text-text-2 group-focus-visible:text-text-2 mt-auto inline-flex items-center gap-1 pt-2.5 text-[0.6875rem] font-semibold opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
        View in Campaigns
        <svg
          viewBox="0 0 16 16"
          width="12"
          height="12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 3l5 5-5 5" />
        </svg>
      </div>
    </button>
  );
}
